/**
 * EmailSettings.jsx - E-Mail Versand für Rechnungen und KVA
 *
 * WICHTIG: Diese Komponente verwaltet SMTP-Einstellungen für den Rechnungs-/KVA-Versand.
 * Die Schlüssel hier (smtp_*) sind UNABHÄNGIG von den KI-Email-Agent Einstellungen!
 *
 * Schlüssel-Trennung:
 * - smtp_* (hier): Rechnungs-/KVA-Versand (EmailSettings.jsx)
 * - email_agent_smtp_* : KI-Email-Agent (EmailAgentSettings.jsx)
 */
import { useState, useEffect } from 'react';
import { Mail, Save, Check, AlertCircle, Eye, EyeOff, Send, FileText, ClipboardList, ExternalLink, CheckCircle, XCircle, Info } from '../../icons/wp-icons';
import { settingsApi } from '../../api/settingsApi';
import { emailOAuthApi } from '../../api/emailOAuthApi';
import { useAuth } from '../../context/AuthContext';
import { firmendatenApi } from '../../api/firmendatenApi';

// Provider Icons als SVG
const GmailIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
  </svg>
);

const MicrosoftIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
    <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
  </svg>
);

function EmailSettings({ readOnly = false }) {
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const [firmenEmail, setFirmenEmail] = useState('');

  // Provider State
  const [providerStatus, setProviderStatus] = useState({
    gmail: { connected: false, email: null },
    outlook: { connected: false, email: null }
  });
  const [connecting, setConnecting] = useState(null);

  // SMTP Einstellungen
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('465');
  const [smtpSecure, setSmtpSecure] = useState(true);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFromName, setSmtpFromName] = useState('');
  const [smtpFromEmail, setSmtpFromEmail] = useState('');
  const [hasPassword, setHasPassword] = useState(false);

  // E-Mail Vorlagen - Rechnungen
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // E-Mail Vorlagen - Kostenvoranschläge
  const [kvaEmailSubject, setKvaEmailSubject] = useState('');
  const [kvaEmailBody, setKvaEmailBody] = useState('');

  // UI State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [activeTab, setActiveTab] = useState('gmail'); // 'gmail', 'outlook', 'smtp', 'template', 'template_kva'

  useEffect(() => {
    loadSettings();
    loadProviderStatus();
    // Firmendaten-E-Mail laden — wird sowohl für den Test-Mail-Empfänger
    // gebraucht als auch für den Disable-Hinweis am Button.
    firmendatenApi.get()
      .then(fd => setFirmenEmail((fd?.email || '').trim()))
      .catch(() => setFirmenEmail(''));
  }, []);

  const loadProviderStatus = async () => {
    try {
      const status = await emailOAuthApi.getAllStatus();
      setProviderStatus({
        gmail: status.gmail || { connected: false, email: null },
        outlook: status.outlook || { connected: false, email: null }
      });
    } catch (error) {
      console.error('Fehler beim Laden des Provider-Status:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const settings = await settingsApi.getAll();

      // WICHTIG: Diese SMTP Einstellungen (smtp_*) sind NUR für den Rechnungs-/KVA-Versand!
      // Der KI-Email-Agent verwendet SEPARATE Einstellungen (email_agent_smtp_*).
      // Diese beiden Konfigurationen sind vollständig unabhängig voneinander.
      if (settings.smtp_host) setSmtpHost(settings.smtp_host);
      if (settings.smtp_port) setSmtpPort(settings.smtp_port);
      if (settings.smtp_secure !== undefined) setSmtpSecure(settings.smtp_secure === 'true');
      if (settings.smtp_user) setSmtpUser(settings.smtp_user);
      if (settings.smtp_from_name) setSmtpFromName(settings.smtp_from_name);
      if (settings.smtp_from_email) setSmtpFromEmail(settings.smtp_from_email);

      // Passwort ist maskiert - prüfen ob eines gesetzt ist
      if (settings.smtp_password && settings.smtp_password !== '********') {
        setSmtpPassword(settings.smtp_password);
        setHasPassword(true);
      } else if (settings.smtp_password === '********') {
        setHasPassword(true);
      }

      // E-Mail Vorlagen - Rechnungen
      if (settings.email_template_rechnung_subject) {
        setEmailSubject(settings.email_template_rechnung_subject);
      }
      if (settings.email_template_rechnung_body) {
        setEmailBody(settings.email_template_rechnung_body);
      }

      // E-Mail Vorlagen - Kostenvoranschläge
      if (settings.email_template_kva_subject) {
        setKvaEmailSubject(settings.email_template_kva_subject);
      }
      if (settings.email_template_kva_body) {
        setKvaEmailBody(settings.email_template_kva_body);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Einstellungen:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (provider) => {
    try {
      setConnecting(provider);

      // WICHTIG: Popup SOFORT öffnen bevor async-Call!
      // Mobile Browser/PWAs blockieren window.open() wenn es nicht direkt durch User-Aktion kommt
      const width = 550;
      const height = 650;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        'about:blank',
        `${provider} OAuth`,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
      );

      // Falls Popup blockiert wurde
      if (!popup) {
        alert('Bitte erlauben Sie Popups für diese Seite, um die Verbindung herzustellen.');
        setConnecting(null);
        return;
      }

      // Lade-Nachricht anzeigen
      popup.document.write('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;background:#f9fafb;"><p>Laden...</p></body></html>');

      // Auth-URL holen und Popup dorthin navigieren
      let authUrl;
      if (provider === 'gmail') {
        authUrl = (await emailOAuthApi.gmail.getAuthUrl()).authUrl;
      } else if (provider === 'outlook') {
        authUrl = (await emailOAuthApi.outlook.getAuthUrl()).authUrl;
      }
      popup.location.href = authUrl;

      // Popup geschlossen? → Status neu laden
      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          setConnecting(null);
          setTimeout(() => loadProviderStatus(), 500);
        }
      }, 500);

    } catch (error) {
      console.error(`Fehler beim Verbinden mit ${provider}:`, error);
      alert(`Fehler beim Verbinden: ${error.message}`);
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!confirm(`${provider === 'gmail' ? 'Gmail' : 'Outlook'} Verbindung wirklich trennen?`)) return;

    try {
      if (provider === 'gmail') {
        await emailOAuthApi.gmail.disconnect();
      } else if (provider === 'outlook') {
        await emailOAuthApi.outlook.disconnect();
      }
      await loadProviderStatus();
    } catch (error) {
      console.error(`Fehler beim Trennen von ${provider}:`, error);
      alert('Fehler beim Trennen der Verbindung');
    }
  };

  const handleSaveSmtp = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      // Whitespace bereinigen um Verbindungsfehler zu vermeiden
      const trimmedHost = smtpHost?.trim() || '';
      const trimmedUser = smtpUser?.trim() || '';
      const trimmedFromEmail = smtpFromEmail?.trim() || '';

      // WICHTIG: Diese SMTP Einstellungen (smtp_*) sind NUR für den Rechnungs-/KVA-Versand!
      // Der KI-Email-Agent verwendet SEPARATE Einstellungen (email_agent_smtp_*).
      const settings = {
        smtp_host: trimmedHost,
        smtp_port: smtpPort,
        smtp_secure: String(smtpSecure),
        smtp_user: trimmedUser,
        smtp_from_name: smtpFromName,
        smtp_from_email: trimmedFromEmail || trimmedUser
      };

      // Passwort nur speichern wenn es geändert wurde
      if (smtpPassword && smtpPassword !== '********') {
        settings.smtp_password = smtpPassword;
      }

      await settingsApi.updateBulk(settings);

      // State mit getrimmten Werten aktualisieren
      setSmtpHost(trimmedHost);
      setSmtpUser(trimmedUser);
      setSmtpFromEmail(trimmedFromEmail);

      setSaved(true);
      setHasPassword(!!smtpPassword);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setError('Fehler beim Speichern der SMTP-Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await settingsApi.updateBulk({
        email_template_rechnung_subject: emailSubject,
        email_template_rechnung_body: emailBody
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setError('Fehler beim Speichern der E-Mail-Vorlage');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKvaTemplate = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await settingsApi.updateBulk({
        email_template_kva_subject: kvaEmailSubject,
        email_template_kva_body: kvaEmailBody
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setError('Fehler beim Speichern der KVA E-Mail-Vorlage');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError('');

    try {
      // Wenn der User eigene Werte eingetragen hat, erst speichern — sonst
      // testet der Backend-Endpoint gegen die alten DB-Werte. Bei HeyKiki-Orgs
      // ohne Eingabe überspringen, damit der Brevo-Fallback direkt greift.
      if (smtpHost && smtpUser) {
        await handleSaveSmtp();
      }

      const result = await settingsApi.testSmtpConnection();
      setTestResult(result);
    } catch (error) {
      console.error('Fehler beim Testen:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.message || error.message
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Provider Tab Content rendern
  const renderProviderTab = (provider) => {
    const status = providerStatus[provider];
    const isConnected = status?.connected;
    const isConnecting = connecting === provider;
    const providerName = provider === 'gmail' ? 'Gmail' : 'Outlook / Microsoft 365';
    const Icon = provider === 'gmail' ? GmailIcon : MicrosoftIcon;
    const buttonColor = 'bg-primary hover:bg-primary-dark';

    return (
      <div className="space-y-4">
        {/* Status Card */}
        <div className={`p-4 rounded-lg border-2 ${
          isConnected ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-white rounded-lg shadow-sm shrink-0">
                <Icon className="w-8 h-8" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900">{providerName}</h3>
                {isConnected && status.email ? (
                  <p className="text-sm text-green-700 truncate">{status.email}</p>
                ) : (
                  <p className="text-sm text-gray-500">Nicht verbunden</p>
                )}
              </div>
            </div>

            {isConnected ? (
              <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium shrink-0 self-start sm:self-auto">
                <CheckCircle className="w-4 h-4 shrink-0" />
                <span className="whitespace-nowrap">Verbunden</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium shrink-0 self-start sm:self-auto">
                <XCircle className="w-4 h-4 shrink-0" />
                <span className="whitespace-nowrap">Nicht verbunden</span>
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Vorteile der {providerName}-Anbindung:</strong>
          </p>
          <ul className="mt-2 text-sm text-blue-800 space-y-1 ml-4 list-disc">
            <li>E-Mails werden direkt über Ihr {providerName}-Konto versendet</li>
            <li>Höhere Zustellungsrate als bei SMTP</li>
            <li>Gesendete E-Mails erscheinen in Ihrem Postausgang</li>
            <li>Keine manuelle Konfiguration notwendig</li>
          </ul>
        </div>

        {/* Action Button - nur anzeigen wenn nicht readOnly */}
        {!readOnly && (
          isConnected ? (
            <button
              onClick={() => handleDisconnect(provider)}
              className="w-full px-4 py-3 bg-red-100 text-red-700 rounded-button hover:bg-red-200 transition font-medium"
            >
              Verbindung trennen
            </button>
          ) : (
            <button
              onClick={() => handleConnect(provider)}
              disabled={isConnecting}
              className={`w-full px-4 py-3 ${buttonColor} text-white rounded-button transition font-medium flex items-center justify-center gap-2 disabled:opacity-50`}
            >
              {isConnecting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Verbinde...
                </>
              ) : (
                <>
                  <ExternalLink className="w-5 h-5" />
                  Mit {providerName} verbinden
                </>
              )}
            </button>
          )
        )}
      </div>
    );
  };

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-primary-light rounded-lg">
          <Mail className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">E-Mail Versand (Rechnungen/KVA)</h2>
          <p className="text-sm text-text-muted">
            Verbinden Sie Gmail, Outlook oder konfigurieren Sie SMTP manuell
          </p>
        </div>
      </div>

      {/* Tabs - Mobile scrollbar, Desktop normal */}
      <div className="flex overflow-x-auto border-b border-border mb-6 -mx-6 px-6 sm:mx-0 sm:px-0 sm:overflow-visible">
        <button
          onClick={() => setActiveTab('gmail')}
          className={`px-3 sm:px-4 py-2 font-medium text-sm border-b-2 transition whitespace-nowrap ${
            activeTab === 'gmail'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          <span className="flex items-center gap-1.5 sm:gap-2">
            <GmailIcon className="w-4 h-4" />
            Gmail
            {providerStatus.gmail?.connected && (
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('outlook')}
          className={`px-3 sm:px-4 py-2 font-medium text-sm border-b-2 transition whitespace-nowrap ${
            activeTab === 'outlook'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          <span className="flex items-center gap-1.5 sm:gap-2">
            <MicrosoftIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Outlook</span>
            <span className="sm:hidden">MS</span>
            {providerStatus.outlook?.connected && (
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('smtp')}
          className={`px-3 sm:px-4 py-2 font-medium text-sm border-b-2 transition whitespace-nowrap ${
            activeTab === 'smtp'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          <span className="flex items-center gap-1.5 sm:gap-2">
            <Send className="w-4 h-4" />
            <span className="hidden sm:inline">SMTP Server</span>
            <span className="sm:hidden">SMTP</span>
            {/* Grüner Haken wenn SMTP aktiv verwendet wird (kein OAuth verbunden) */}
            {!providerStatus.gmail?.connected && !providerStatus.outlook?.connected && smtpHost && smtpUser && (
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('template')}
          className={`px-3 sm:px-4 py-2 font-medium text-sm border-b-2 transition whitespace-nowrap ${
            activeTab === 'template'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          <span className="flex items-center gap-1.5 sm:gap-2">
            <FileText className="w-4 h-4" />
            Rechnungen
          </span>
        </button>
        <button
          onClick={() => setActiveTab('template_kva')}
          className={`px-3 sm:px-4 py-2 font-medium text-sm border-b-2 transition whitespace-nowrap ${
            activeTab === 'template_kva'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          <span className="flex items-center gap-1.5 sm:gap-2">
            <ClipboardList className="w-4 h-4" />
            <span className="hidden sm:inline">Kostenvoranschläge</span>
            <span className="sm:hidden">KVA</span>
          </span>
        </button>
      </div>

      {/* Gmail Tab */}
      {activeTab === 'gmail' && renderProviderTab('gmail')}

      {/* Outlook Tab */}
      {activeTab === 'outlook' && renderProviderTab('outlook')}

      {/* SMTP Tab */}
      {activeTab === 'smtp' && (
        <div className="space-y-4">
          {/* HeyKiki-Hinweis: wenn keine eigenen Werte, läuft der Versand
              über den HeyKiki-Standard (Brevo / info@kiki-zusammenfassung.de).
              Der Test-Mail-Empfänger ist IMMER die Firmendaten-E-Mail unter
              Einstellungen → Allgemein. */}
          {isHeykikiOrg && !smtpHost && !smtpUser && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Standard-Versand über HeyKiki aktiv</p>
                  <p className="mt-1">
                    Solange Sie keinen eigenen SMTP-Server eintragen, werden
                    Rechnungen und Kostenvoranschläge automatisch über den
                    HeyKiki-Standardversand verschickt (Absender
                    <code className="mx-1 px-1 py-0.5 bg-blue-100 rounded">info@kiki-zusammenfassung.de</code>).
                  </p>
                  <p className="mt-2">
                    Mit „Test-Mail senden" verschicken wir eine echte Test-Mail
                    an Ihre Firmen-E-Mail
                    {firmenEmail ? (
                      <> (<code className="mx-1 px-1 py-0.5 bg-blue-100 rounded">{firmenEmail}</code>)</>
                    ) : (
                      <> — dafür müssen Sie zuerst unter <strong>Einstellungen → Allgemein → Firmendaten</strong> eine E-Mail-Adresse hinterlegen.</>
                    )}.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Status-Anzeige: Wird SMTP verwendet oder nicht? */}
          {(providerStatus.gmail?.connected || providerStatus.outlook?.connected) ? (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                  {providerStatus.gmail?.connected ? (
                    <GmailIcon className="w-5 h-5" />
                  ) : (
                    <MicrosoftIcon className="w-5 h-5" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-blue-900">
                    E-Mails werden über {providerStatus.gmail?.connected ? 'Gmail' : 'Outlook'} versendet
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    SMTP wird aktuell <strong>nicht verwendet</strong>, da {providerStatus.gmail?.connected ? 'Gmail' : 'Outlook'} verbunden ist.
                    Die SMTP-Einstellungen dienen nur als Fallback.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg shrink-0">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-900">
                    SMTP wird für den E-Mail-Versand verwendet
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Kein OAuth-Provider (Gmail/Outlook) verbunden. Rechnungen und KVAs werden über diese SMTP-Einstellungen versendet.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Host */}
            <div>
              <label className="block text-sm font-medium mb-1">
                SMTP Server
              </label>
              <input
                type="text"
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                placeholder="smtp.gmail.com"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Port */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Port
              </label>
              <input
                type="text"
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
                placeholder="465"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Benutzername */}
          <div>
            <label className="block text-sm font-medium mb-1">
              E-Mail / Benutzername
            </label>
            <input
              type="email"
              value={smtpUser}
              onChange={(e) => setSmtpUser(e.target.value)}
              placeholder="ihre@email.de"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Passwort */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Passwort / App-Passwort
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={smtpPassword}
                onChange={(e) => setSmtpPassword(e.target.value)}
                placeholder={hasPassword ? '••••••••••••' : 'Passwort eingeben'}
                className="w-full px-3 py-2 pr-10 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-text-muted mt-1">
              Bei Gmail: App-Passwort verwenden (nicht das normale Passwort)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Absendername */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Absendername
              </label>
              <input
                type="text"
                value={smtpFromName}
                onChange={(e) => setSmtpFromName(e.target.value)}
                placeholder="Ihre Firma"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Absender-E-Mail */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Absender-E-Mail (optional)
              </label>
              <input
                type="email"
                value={smtpFromEmail}
                onChange={(e) => setSmtpFromEmail(e.target.value)}
                placeholder={smtpUser || 'Falls abweichend von Benutzername'}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* SSL Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium">SSL/TLS verwenden</p>
              <p className="text-xs text-text-muted">Port 465 = SSL, Port 587 = TLS/STARTTLS</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={smtpSecure}
                onChange={(e) => setSmtpSecure(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
              testResult.success
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {testResult.success ? (
                <Check className="w-4 h-4 shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 shrink-0" />
              )}
              {testResult.message}
            </div>
          )}

          {/* Buttons - nur anzeigen wenn nicht readOnly */}
          {!readOnly && (
            <div className="flex gap-3">
              <button
                onClick={handleTestConnection}
                disabled={
                  testing
                  || !firmenEmail
                  || (!isHeykikiOrg && (!smtpHost || !smtpUser))
                }
                className="flex items-center justify-center gap-2 px-4 py-2 border border-primary text-primary rounded-button font-medium hover:bg-primary-light transition disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  !firmenEmail
                    ? 'Bitte zuerst unter Einstellungen → Allgemein → Firmendaten eine E-Mail hinterlegen.'
                    : `Test-Mail geht an ${firmenEmail}`
                }
              >
                {testing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Sende Test-Mail...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Test-Mail senden
                  </>
                )}
              </button>

              <button
                onClick={handleSaveSmtp}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Einstellungen speichern
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Template Tab */}
      {activeTab === 'template' && (
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <p className="font-medium mb-1">Verfügbare Platzhalter:</p>
            <code className="text-xs">
              {'{kundename}'} {'{kundennummer}'} {'{rechnungsnummer}'} {'{datum}'} {'{betrag}'} {'{faelligDatum}'} {'{firmenname}'} {'{strasse}'} {'{plz}'} {'{ort}'} {'{telefon}'} {'{firmenemail}'}
            </code>
          </div>

          {/* Betreff */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Betreff
            </label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Ihre Rechnung {rechnungsnummer} von {firmenname}"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Nachricht */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Nachricht
            </label>
            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder={`Sehr geehrte/r {kundename},

anbei erhalten Sie Ihre Rechnung {rechnungsnummer} vom {datum} über {betrag}.
Ihre Kundennummer: {kundennummer}

Bitte überweisen Sie den Betrag bis zum {faelligDatum} auf unser Konto.

Mit freundlichen Grüßen
{firmenname}`}
              rows={12}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            />
          </div>

          {/* Save Button */}
          {!readOnly && (
            <button
              onClick={handleSaveTemplate}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Einstellungen speichern
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* KVA Template Tab */}
      {activeTab === 'template_kva' && (
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <p className="font-medium mb-1">Verfügbare Platzhalter:</p>
            <code className="text-xs">
              {'{kundename}'} {'{kundennummer}'} {'{kvanummer}'} {'{datum}'} {'{betrag}'} {'{gueltigBis}'} {'{anfrage}'} {'{firmenname}'} {'{strasse}'} {'{plz}'} {'{ort}'} {'{telefon}'} {'{firmenemail}'}
            </code>
          </div>

          {/* Betreff */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Betreff
            </label>
            <input
              type="text"
              value={kvaEmailSubject}
              onChange={(e) => setKvaEmailSubject(e.target.value)}
              placeholder="Ihr Kostenvoranschlag {kvanummer} von {firmenname}"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Nachricht */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Nachricht
            </label>
            <textarea
              value={kvaEmailBody}
              onChange={(e) => setKvaEmailBody(e.target.value)}
              placeholder={`Sehr geehrte/r {kundename},

anbei erhalten Sie Ihren Kostenvoranschlag {kvanummer} vom {datum} über {betrag} zu Ihrer Anfrage "{anfrage}".
Ihre Kundennummer: {kundennummer}

Dieser Kostenvoranschlag ist gültig bis zum {gueltigBis} und unverbindlich.

Bei Fragen oder für eine Auftragserteilung stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
{firmenname}`}
              rows={12}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            />
          </div>

          {/* Save Button */}
          {!readOnly && (
            <button
              onClick={handleSaveKvaTemplate}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Einstellungen speichern
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {saved && (
        <div className="flex items-center gap-2 p-3 mt-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          <Check className="w-4 h-4 shrink-0" />
          Einstellungen erfolgreich gespeichert
        </div>
      )}
    </div>
  );
}

export default EmailSettings;
